import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import StickySidebar from '../../../../plugins/stickySidebarPlugin';
import Picture from '../../components/ui/Picture/Picture';
import Dialog from '../../components/Dialog/Dialog';
import Wishlist from './Wishlist';
import Sizes from './Sizes';
import ProductContext from '../../contexts/ProductContext';
import {
  ArrowRight,
  Calendar,
  Lock,
  Question,
} from '../../components/ui/Icons';
import ProductPreview from './ProductPreview';
import Form from '../../components/Form/Form';
import SidebarLink from './SidebarLink';
import Price from './Price';
import { MandatoryVariant, Size } from '../../types/components';
import AddToCart from './AddToCart';
import BackInStock from './BackInStock';
import MandatoryVariants from './MandatoryVariants';
import SizeGuideSidebar from './SizeGuideSidebar';

export default function Sidebar({
  selectedColor,
  handleSelectedColorChange,
  type = 'full',
}: {
  selectedColor: string,
  handleSelectedColorChange: (color: string) => void,
  type: 'full' | 'small',
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    product,
    sizes,
    mandatoryVariants,
    translates,
  } = useContext(ProductContext);
  const { products } = translates;
  const fixedSidebar = useRef<HTMLDivElement | null>(null);

  const [showMoreColors, setShowMoreColors] = useState<boolean>(false);

  const [selectedSize, setSelectedSize] = useState<Size>(Object.values(sizes[selectedColor])
    .find((size) => size.hasStock) || sizes[selectedColor][0]);

  const [selectedMandatoryVariant, setSelectedMandatoryVariant] = useState<MandatoryVariant | null>(
    mandatoryVariants ? mandatoryVariants[0] : null,
  );

  const [selectedVariant, setSelectedVariant] = useState<number>(selectedSize.id);

  const [selectedPrices, setSelectedPrices] = useState({
    price: sizes[selectedColor][0].price
      + (selectedMandatoryVariant !== null ? selectedMandatoryVariant.price : 0),
    promotionalPrice: sizes[selectedColor][0].promotionalPrice
      + (selectedMandatoryVariant !== null
        ? selectedMandatoryVariant.salePrice : 0),
    onPromotion: sizes[selectedColor][0].onPromotion,
  });

  const handleSizeChange = (newSize: Size) => setSelectedSize(newSize);

  const handleVariantChange = (newVariant: number) => setSelectedVariant(newVariant);

  // eslint-disable-next-line max-len
  const handleMandatoryVariantChange = (newVariant: MandatoryVariant) => setSelectedMandatoryVariant(newVariant);

  const handlePricesChange = (priceObject: {
    price: string,
    promotionalPrice: string,
    onPromotion: boolean,
  }) => setSelectedPrices(priceObject);

  useEffect(() => {
    setSelectedSize(Object.values(sizes[selectedColor])
      .find((size) => size.hasStock) || sizes[selectedColor][0]);
    setSelectedVariant(selectedSize.id);
    setSelectedPrices({
      price: selectedSize.price
        + (selectedMandatoryVariant !== null ? selectedMandatoryVariant.price : 0),
      promotionalPrice: selectedSize.promotionalPrice
        + (selectedMandatoryVariant !== null ? selectedMandatoryVariant.salePrice : 0),
      onPromotion: selectedSize.onPromotion,
    });
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    selectedColor,
    selectedMandatoryVariant,
    sizes,
  ]);

  useEffect(() => {
    if (fixedSidebar.current && type === 'full') {
      const header = document.getElementById('header');
      const content = fixedSidebar.current.querySelector('.product-sidebar-inner');

      const stickySidebar = new StickySidebar(fixedSidebar.current, {
        topSpacing: header?.clientHeight,
        bottomSpacing: 0,
        containerSelector: '.fixed-product-sidebar',
      });

      const resizeObserver = new ResizeObserver(() => stickySidebar.updateSticky());

      if (content) {
        resizeObserver.observe(content);
      }
    }
  }, [fixedSidebar, selectedColor, type]);

  return (
    <aside className="product-sidebar fixed-product-sidebar lg:px-6 xl:px-11.7">
      <div id="js-product-sidebar-fixed" ref={fixedSidebar}>
        <div className="inner-wrapper-sticky pt-6 lg:pb-8 xl:py-8">
          <div className="product-sidebar-inner">
            <div className="mb-8 last:mb-0">
              {product.brand && (
                <span className="block uppercase text-base lg:text-lg tracking-widest mb-1 lg:mb-0">{product.brand}</span>
              )}
              <h2 className="leading-snug mb-3 lg:mb-4">{product.title}</h2>
              <Price
                priceState={selectedPrices}
              />
            </div>
            <div className="mb-8 last:mb-0">
              {product.colors && Object.keys(product.colors).length > 1 && (
                <>
                  <h3 className="font-semibold text-base lg:text-lg mb-1">{Craft.t(products, 'Kleur')}</h3>
                  <ul className="option-list group mb-6 lg:mb-8 flex flex-wrap relative pt-8.5 lg:pt-8.7 leading-tight text-sm lg:text-base text-light-grey-3 -mx-1.5 gap-y-6">
                    {Object.values(product.colors).map((color, index) => (
                      <li className={`option-box w-1/4 px-1.5 group/option-box ${index > 2 && !showMoreColors ? 'hidden' : ''}`} key={color.color}>
                        <span className={`option-box-text absolute top-0 left-1.5 right-0 truncate transition-all bg-white z-10 ${color.color === selectedColor ? 'opacity-100 visible' : 'opacity-0 invisible group-hover/option-box:opacity-100 group-hover/option-box:visible'}`}>{color.color}</span>
                        <div className={`p-1.5 rounded cursor-pointer bg-white transition-all border-2 border-light-grey-2 hover:border-near-black ${color.color === selectedColor ? 'border-near-black' : ''}`}>
                          <button
                            type="button"
                            className="prose block relative overflow-hidden aspect-4/2.8 h-full w-full"
                            onClick={() => {
                              handleSelectedColorChange(color.color);
                              searchParams.set('kleur', color.color);
                              setSearchParams(searchParams);
                            }}
                          >
                            {color.image ? (
                              <Picture image={color.image} loading="eager" classes="absolute inset-0 w-full h-full object-contain" />
                            ) : <span>{color.color}</span>}
                          </button>
                        </div>
                      </li>
                    ))}
                    {Object.keys(product.colors).length > 3 && !showMoreColors && (
                      <li className="w-1/4 px-1.5">
                        <button
                          type="button"
                          className="block w-full p-1.5 rounded cursor-pointer bg-white transition-all border-2 border-light-grey-2 hover:border-near-black"
                          onClick={() => setShowMoreColors(true)}
                        >
                          <span className="relative overflow-hidden aspect-4/2.8 text-base lg:text-lg font-semibold text-near-black flex justify-center items-center">
                            {`+ ${Object.keys(product.colors).length - 3}`}
                          </span>
                        </button>
                      </li>
                    )}
                  </ul>
                </>
              )}
              {sizes && (
                <Sizes
                  selectedColor={selectedColor}
                  selectedSize={selectedSize}
                  selectedMandatoryVariant={selectedMandatoryVariant}
                  handleSizeChange={handleSizeChange}
                  handleVariantChange={handleVariantChange}
                  handlePricesChange={handlePricesChange}
                />
              )}
              {mandatoryVariants && selectedMandatoryVariant && (
                <MandatoryVariants
                  selectedMandatoryVariant={selectedMandatoryVariant}
                  selectedSize={selectedSize}
                  handleMandatoryVariantChange={handleMandatoryVariantChange}
                  handlePricesChange={handlePricesChange}
                />
              )}
              {product.isQuoteOnly && product.quoteLink && (
                <div>
                  {selectedSize.hasStock ? (
                    <div className="flex gap-x-3">
                      {product.quoteLink.type === 'form' && product.quoteLink.formHandle && (
                        <Dialog
                          buttonClasses="btn btn-secondary flex-1.0 justify-between"
                          buttonContent={(
                            <>
                              {product.quoteLink.linkText}
                              <ArrowRight />
                            </>
                          )}
                          dialogTitle={product.quoteLink.dialogTitle}
                          dialogContent={(
                            <>
                              <ProductPreview
                                title={product.title}
                                color={selectedColor}
                                image={product.colors[selectedColor]?.image}
                              />
                              {product.quoteLink.dialogDescription && (
                                // eslint-disable-next-line react/no-danger
                                <div className="mb-6 sm:mb-8" dangerouslySetInnerHTML={{ __html: product.quoteLink.dialogDescription }} />
                              )}
                              <Form
                                siteId={product.siteId}
                                selectedVariant={selectedVariant}
                                selectedMandatoryVariant={selectedMandatoryVariant}
                                formHandle={product.quoteLink.formHandle}
                              />
                            </>
                          )}
                        />
                      )}
                      {product.isQuoteOnly && product.quoteLink && product.quoteLink.type === 'url' && (
                        // eslint-disable-next-line react/jsx-no-target-blank
                        <a
                          href={product.quoteLink.url}
                          className="btn btn-secondary flex-1.0 justify-between"
                          target={product.quoteLink.newWindow ? '_blank' : '_self'}
                        >
                          {product.quoteLink.linkText}
                          <ArrowRight />
                        </a>
                      )}
                      <Wishlist selectedVariant={selectedVariant} />
                    </div>
                  ) : (
                    <BackInStock selectedVariant={selectedVariant} />
                  )}
                </div>
              )}
              {!product.isQuoteOnly && (
                <div>
                  {selectedSize.hasStock ? (
                    <div className="flex gap-x-3">
                      <AddToCart selectedVariant={selectedVariant} />
                      <Wishlist selectedVariant={selectedVariant} />
                    </div>
                  ) : (
                    <BackInStock selectedVariant={selectedVariant} />
                  )}
                </div>
              )}
            </div>
            <div className="mb-6 lg:mb-8 last:mb-0">
              <ul className="text-sm lg:text-base">
                {product.sizeGuide && (
                  <SizeGuideSidebar guide={product.sizeGuide} />
                )}
                <SidebarLink
                  siteId={product.siteId}
                  link={product.questionLink}
                  icon={(
                    <Question />
                  )}
                  productTitle={product.title}
                  selectedColor={selectedColor}
                  selectedVariant={selectedVariant}
                  selectedMandatoryVariant={selectedMandatoryVariant}
                  image={product.colors[selectedColor]?.image}
                />
                <SidebarLink
                  siteId={product.siteId}
                  link={product.testRideLink}
                  icon={(
                    <Calendar />
                  )}
                  productTitle={product.title}
                  selectedColor={selectedColor}
                  selectedVariant={selectedVariant}
                  selectedMandatoryVariant={selectedMandatoryVariant}
                  image={product.colors[selectedColor]?.image}
                />
                <SidebarLink
                  siteId={product.siteId}
                  link={product.bikeLeaseLink}
                  icon={(
                    <Lock />
                  )}
                  productTitle={product.title}
                  selectedColor={selectedColor}
                  selectedVariant={selectedVariant}
                  selectedMandatoryVariant={selectedMandatoryVariant}
                  image={product.colors[selectedColor]?.image}
                />
              </ul>
            </div>
            <div className="hidden lg:block">
              <a href="#product-info" className="btn btn-link btn-link-sm btn-link-down">
                <ArrowRight className="rotate-90" />
                {Craft.t(products, 'Meer informatie & Specificaties')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
